import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileDown,
  Loader2,
  RefreshCw,
  Search,
  TrendingDown,
} from 'lucide-react';

import {
  getMarginAudit,
  type MarginAuditResponse,
  type MarginAuditRow,
  type MarginAuditMsrpFlag,
} from '../../services/api';

// --------------------------------------------------------------------------
// This page is designed to lift out of the Pax8 Licensing portal and into an
// OverSite V3 Pax8 connector. Umetech is the first MSP tenant that will host
// the connector — matching that migration path means:
//   (a) the page reads a single MarginAuditResponse contract and derives all
//       views from it (no cross-references to portal-only state / Cosmos)
//   (b) all rollups (by SKU / by client / by renewal window) are computed
//       here client-side from the row list so a connector serving the same
//       response shape produces the same page verbatim
//   (c) styling stays on the design-tokens palette (bg-card / border-border /
//       text-muted-foreground / *-foreground variables) so a theme swap on
//       OverSite paints correctly with no code changes.
// --------------------------------------------------------------------------

type TabKey = 'bySku' | 'byClient' | 'byRenewal' | 'lines';

function fmtMoney(n: number, digits = 2): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fmtPct(n: number, digits = 2): string {
  return `${n.toFixed(digits)}%`;
}

function fmtMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

function msrpBadge(flag: MarginAuditMsrpFlag): { label: string; cls: string } {
  switch (flag) {
    case 'AT_MSRP':      return { label: 'At MSRP',      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
    case 'ABOVE_MSRP':   return { label: 'Above MSRP',   cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
    case 'BELOW_MSRP':   return { label: 'Below MSRP',   cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' };
    case 'NO_MSRP':      return { label: 'No MSRP',      cls: 'bg-muted text-muted-foreground' };
  }
}

function StatTile({
  label, value, sub, valueClass, icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="border border-border rounded-lg bg-card p-4">
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        {Icon && <Icon className="h-4 w-4" />}
      </div>
      <div className={`text-2xl font-bold mt-2 ${valueClass ?? 'text-foreground'}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

interface SkuRollup {
  sku: string;
  vendor: string;
  lines: number;
  seats: number;
  monthlyBill: number;
  monthlyCost: number;
  monthlyMargin: number;
  belowCostLines: number;
  belowMsrpLines: number;
  monthlyBleedIfBelowCost: number;
  monthlyLeftOnTableIfBelowMsrp: number;
}

interface ClientRollup {
  companyId: string;
  companyName: string;
  lines: number;
  monthlyBill: number;
  monthlyCost: number;
  monthlyMargin: number;
  marginPct: number;
  belowCostLines: number;
  belowMsrpLines: number;
  monthlyBleedIfBelowCost: number;
  monthlyLeftOnTableIfBelowMsrp: number;
}

interface RenewalBucket {
  monthKey: string; // YYYY-MM or 'no-commit'
  monthLabel: string;
  lines: number;
  monthlyBleed: number;
  monthlyLeftOnTable: number;
}

function rollupBySku(rows: MarginAuditRow[]): SkuRollup[] {
  const acc = new Map<string, SkuRollup>();
  for (const r of rows) {
    const key = r.productName;
    if (!acc.has(key)) {
      acc.set(key, {
        sku: key, vendor: r.vendor, lines: 0, seats: 0,
        monthlyBill: 0, monthlyCost: 0, monthlyMargin: 0,
        belowCostLines: 0, belowMsrpLines: 0,
        monthlyBleedIfBelowCost: 0, monthlyLeftOnTableIfBelowMsrp: 0,
      });
    }
    const s = acc.get(key)!;
    s.lines++;
    s.seats += r.quantity;
    s.monthlyBill += r.monthlyBill;
    s.monthlyCost += r.monthlyCost;
    s.monthlyMargin += r.monthlyMargin;
    if (r.belowCost) {
      s.belowCostLines++;
      s.monthlyBleedIfBelowCost += r.monthlyCost - r.monthlyBill;
    }
    if (r.msrpFlag === 'BELOW_MSRP' && r.unitMsrp !== null) {
      s.belowMsrpLines++;
      s.monthlyLeftOnTableIfBelowMsrp += (r.unitMsrp - r.unitSellPrice) * r.quantity;
    }
  }
  return Array.from(acc.values());
}

function rollupByClient(rows: MarginAuditRow[]): ClientRollup[] {
  const acc = new Map<string, ClientRollup>();
  for (const r of rows) {
    if (!acc.has(r.companyId)) {
      acc.set(r.companyId, {
        companyId: r.companyId, companyName: r.companyName,
        lines: 0, monthlyBill: 0, monthlyCost: 0, monthlyMargin: 0, marginPct: 0,
        belowCostLines: 0, belowMsrpLines: 0,
        monthlyBleedIfBelowCost: 0, monthlyLeftOnTableIfBelowMsrp: 0,
      });
    }
    const c = acc.get(r.companyId)!;
    c.lines++;
    c.monthlyBill += r.monthlyBill;
    c.monthlyCost += r.monthlyCost;
    c.monthlyMargin += r.monthlyMargin;
    if (r.belowCost) {
      c.belowCostLines++;
      c.monthlyBleedIfBelowCost += r.monthlyCost - r.monthlyBill;
    }
    if (r.msrpFlag === 'BELOW_MSRP' && r.unitMsrp !== null) {
      c.belowMsrpLines++;
      c.monthlyLeftOnTableIfBelowMsrp += (r.unitMsrp - r.unitSellPrice) * r.quantity;
    }
  }
  for (const c of acc.values()) {
    c.marginPct = c.monthlyBill > 0 ? (c.monthlyMargin / c.monthlyBill) * 100 : 0;
  }
  return Array.from(acc.values());
}

function rollupByRenewal(rows: MarginAuditRow[]): RenewalBucket[] {
  const acc = new Map<string, RenewalBucket>();
  for (const r of rows) {
    if (!r.belowCost && r.msrpFlag !== 'BELOW_MSRP') continue;
    const monthKey = r.renewalDate ? r.renewalDate.slice(0, 7) : 'no-commit';
    if (!acc.has(monthKey)) {
      acc.set(monthKey, {
        monthKey,
        monthLabel: monthKey === 'no-commit' ? 'No commitment' : fmtMonth(r.renewalDate),
        lines: 0, monthlyBleed: 0, monthlyLeftOnTable: 0,
      });
    }
    const b = acc.get(monthKey)!;
    b.lines++;
    if (r.belowCost) b.monthlyBleed += r.monthlyCost - r.monthlyBill;
    if (r.msrpFlag === 'BELOW_MSRP' && r.unitMsrp !== null) {
      b.monthlyLeftOnTable += (r.unitMsrp - r.unitSellPrice) * r.quantity;
    }
  }
  return Array.from(acc.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}

// Expanded-row detail table — renders the individual subscription lines that
// contributed to a rollup row. Hides whichever column the outer grouping already
// carries (product on By SKU, client on By Client) to keep the drilldown clean.
function LineDetailTable({
  rows,
  hideProduct = false,
  hideClient = false,
  colSpan,
}: {
  rows: MarginAuditRow[];
  hideProduct?: boolean;
  hideClient?: boolean;
  colSpan: number;
}) {
  const sorted = [...rows].sort((a, b) => {
    if (a.belowCost !== b.belowCost) return a.belowCost ? -1 : 1;
    return a.monthlyMargin - b.monthlyMargin;
  });
  return (
    <tr>
      <td colSpan={colSpan} className="p-0 bg-muted/40 border-t border-border">
        <div className="p-3">
          <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            {sorted.length} subscription {sorted.length === 1 ? 'line' : 'lines'}
          </div>
          <div className="overflow-x-auto border border-border rounded-md bg-card">
            <table className="w-full text-xs">
              <thead className="bg-background text-muted-foreground text-left">
                <tr>
                  {!hideClient  && <th className="px-2 py-1.5 font-medium">Client</th>}
                  {!hideProduct && <th className="px-2 py-1.5 font-medium">Product</th>}
                  <th className="px-2 py-1.5 font-medium text-right">Qty</th>
                  <th className="px-2 py-1.5 font-medium text-right">Sell</th>
                  <th className="px-2 py-1.5 font-medium text-right">Cost</th>
                  <th className="px-2 py-1.5 font-medium text-right">MSRP</th>
                  <th className="px-2 py-1.5 font-medium text-right">Monthly bill</th>
                  <th className="px-2 py-1.5 font-medium text-right">Monthly cost</th>
                  <th className="px-2 py-1.5 font-medium text-right">Monthly margin</th>
                  <th className="px-2 py-1.5 font-medium text-right">Margin %</th>
                  <th className="px-2 py-1.5 font-medium">MSRP</th>
                  <th className="px-2 py-1.5 font-medium">Commit</th>
                  <th className="px-2 py-1.5 font-medium">Renews</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => {
                  const badge = msrpBadge(r.msrpFlag);
                  return (
                    <tr key={r.subscriptionId} className={`border-t border-border ${r.belowCost ? 'bg-red-50/60 dark:bg-red-900/15' : ''}`}>
                      {!hideClient  && <td className="px-2 py-1.5 text-foreground whitespace-nowrap">{r.companyName}</td>}
                      {!hideProduct && <td className="px-2 py-1.5 text-foreground">{r.productName}</td>}
                      <td className="px-2 py-1.5 text-right text-foreground">{r.quantity}</td>
                      <td className="px-2 py-1.5 text-right text-foreground">{fmtMoney(r.unitSellPrice, 4)}</td>
                      <td className="px-2 py-1.5 text-right text-foreground">{fmtMoney(r.unitCost, 4)}</td>
                      <td className="px-2 py-1.5 text-right text-foreground">{r.unitMsrp === null ? '—' : fmtMoney(r.unitMsrp, 4)}</td>
                      <td className="px-2 py-1.5 text-right text-foreground">{fmtMoney(r.monthlyBill)}</td>
                      <td className="px-2 py-1.5 text-right text-foreground">{fmtMoney(r.monthlyCost)}</td>
                      <td className={`px-2 py-1.5 text-right ${r.monthlyMargin < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-foreground'}`}>
                        {fmtMoney(r.monthlyMargin)}
                      </td>
                      <td className={`px-2 py-1.5 text-right ${r.marginPct < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                        {fmtPct(r.marginPct)}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{r.commitmentTerm || '—'}</td>
                      <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{fmtMonth(r.renewalDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
}

function rowsToCsv(rows: MarginAuditRow[]): string {
  const header = [
    'companyName', 'companyId', 'productName', 'vendor', 'productId',
    'subscriptionId', 'status', 'quantity', 'billingTerm', 'commitmentTerm',
    'renewalDate', 'unitSellPrice', 'unitCost', 'unitMsrp',
    'monthlyBill', 'monthlyCost', 'monthlyMargin', 'marginPct', 'pctOfMsrp',
    'msrpFlag', 'belowCost',
  ];
  const esc = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const out = [header.join(',')];
  for (const r of rows) {
    out.push([
      r.companyName, r.companyId, r.productName, r.vendor, r.productId,
      r.subscriptionId, r.status, r.quantity, r.billingTerm, r.commitmentTerm,
      r.renewalDate, r.unitSellPrice.toFixed(4), r.unitCost.toFixed(4),
      r.unitMsrp === null ? '' : r.unitMsrp.toFixed(4),
      r.monthlyBill.toFixed(2), r.monthlyCost.toFixed(2), r.monthlyMargin.toFixed(2),
      r.marginPct.toFixed(2), r.pctOfMsrp === null ? '' : r.pctOfMsrp.toFixed(2),
      r.msrpFlag, r.belowCost ? 'YES' : 'NO',
    ].map(esc).join(','));
  }
  return out.join('\n');
}

export default function MarginAuditPage() {
  const [data, setData] = useState<MarginAuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('bySku');
  const [belowCostOnly, setBelowCostOnly] = useState(false);
  const [belowMsrpOnly, setBelowMsrpOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Row expansion — namespaced per tab so switching tabs preserves state per view.
  const [expandedSku, setExpandedSku] = useState<Set<string>>(new Set());
  const [expandedClient, setExpandedClient] = useState<Set<string>>(new Set());
  const [expandedRenewal, setExpandedRenewal] = useState<Set<string>>(new Set());
  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getMarginAudit();
      setData(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load margin audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    let rows = data.rows;
    if (belowCostOnly) rows = rows.filter(r => r.belowCost);
    if (belowMsrpOnly) rows = rows.filter(r => r.msrpFlag === 'BELOW_MSRP');
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter(r =>
        r.companyName.toLowerCase().includes(q) ||
        r.productName.toLowerCase().includes(q) ||
        r.vendor.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, belowCostOnly, belowMsrpOnly, query]);

  const skuRollups = useMemo(() => {
    const rollups = rollupBySku(filteredRows);
    rollups.sort((a, b) => sortDir === 'asc' ? a.monthlyBleedIfBelowCost - b.monthlyBleedIfBelowCost : b.monthlyBleedIfBelowCost - a.monthlyBleedIfBelowCost);
    return rollups;
  }, [filteredRows, sortDir]);

  const clientRollups = useMemo(() => {
    const rollups = rollupByClient(filteredRows);
    rollups.sort((a, b) => sortDir === 'asc' ? a.monthlyBleedIfBelowCost - b.monthlyBleedIfBelowCost : b.monthlyBleedIfBelowCost - a.monthlyBleedIfBelowCost);
    return rollups;
  }, [filteredRows, sortDir]);

  const renewalBuckets = useMemo(() => rollupByRenewal(filteredRows), [filteredRows]);

  const totalBleed = useMemo(() => filteredRows
    .filter(r => r.belowCost)
    .reduce((s, r) => s + (r.monthlyCost - r.monthlyBill), 0), [filteredRows]);
  const totalLeftOnTable = useMemo(() => filteredRows
    .filter(r => r.msrpFlag === 'BELOW_MSRP' && r.unitMsrp !== null)
    .reduce((s, r) => s + ((r.unitMsrp as number) - r.unitSellPrice) * r.quantity, 0), [filteredRows]);

  function downloadCsv() {
    if (!data) return;
    const csv = rowsToCsv(filteredRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pax8-margin-audit-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Walking every Pax8 subscription… this run takes ~30-45s.
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="border border-red-300 bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-red-800 dark:text-red-200">
          <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-5 w-5" /> Failed to load margin audit</div>
          <div className="text-sm mt-1">{error}</div>
          <button onClick={load} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-100 dark:bg-red-800/50 hover:bg-red-200 dark:hover:bg-red-800 text-sm">
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const s = data.summary;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pax8 margin audit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Compares every in-scope subscription against Pax8 catalog MSRP and partner cost — surfaces below-cost lines and money left on the table across the book.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Generated {new Date(data.generatedAt).toLocaleString()} · walked in {(data.durationMs / 1000).toFixed(1)}s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-background hover:bg-accent text-sm text-foreground">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={downloadCsv} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 text-sm">
            <FileDown className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {s.belowCostLines > 0 && (
        <div className="border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-900 dark:text-red-100">
                {s.belowCostLines} subscription {s.belowCostLines === 1 ? 'line is' : 'lines are'} being billed BELOW partner cost.
              </div>
              <div className="text-sm text-red-800 dark:text-red-200 mt-1">
                Net loss: {fmtMoney(totalBleed)}/mo ({fmtMoney(totalBleed * 12)}/yr). See the "By Client" tab for who to talk to first.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatTile label="Companies" value={s.companiesScanned.toLocaleString()} icon={DollarSign} />
        <StatTile label="In-scope lines" value={s.subscriptionsInScope.toLocaleString()} sub={`${s.uniqueProducts} unique SKUs`} />
        <StatTile label="Monthly revenue" value={fmtMoney(s.monthlyBill)} />
        <StatTile label="Monthly cost" value={fmtMoney(s.monthlyCost)} />
        <StatTile label="Monthly margin" value={fmtMoney(s.monthlyMargin)} sub={fmtPct(s.marginPct)} valueClass={s.marginPct < 5 ? 'text-red-600 dark:text-red-400' : 'text-foreground'} />
        <StatTile label="Below-cost lines" value={s.belowCostLines.toLocaleString()} sub={`${s.belowMsrpLines} below-MSRP`} valueClass={s.belowCostLines > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter by client, product, or vendor…"
            className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={belowCostOnly} onChange={e => setBelowCostOnly(e.target.checked)} />
          Below cost only
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={belowMsrpOnly} onChange={e => setBelowMsrpOnly(e.target.checked)} />
          Below MSRP only
        </label>
        <button
          onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
          className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded-md bg-background text-sm text-foreground hover:bg-accent"
          title="Toggle sort direction for rollups"
        >
          {sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          Bleed
        </button>
      </div>

      <div className="border-b border-border flex gap-2">
        {(['bySku', 'byClient', 'byRenewal', 'lines'] as TabKey[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'bySku'     && `By SKU (${skuRollups.length})`}
            {t === 'byClient'  && `By Client (${clientRollups.length})`}
            {t === 'byRenewal' && `By Renewal (${renewalBuckets.length})`}
            {t === 'lines'     && `All Lines (${filteredRows.length})`}
          </button>
        ))}
      </div>

      {tab === 'bySku' && (
        <div className="border border-border rounded-lg bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium">Vendor</th>
                  <th className="px-3 py-2 font-medium text-right">Lines</th>
                  <th className="px-3 py-2 font-medium text-right">Seats</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly bill</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly cost</th>
                  <th className="px-3 py-2 font-medium text-right">Margin</th>
                  <th className="px-3 py-2 font-medium text-right">Below cost</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly bleed</th>
                  <th className="px-3 py-2 font-medium text-right">Below MSRP</th>
                  <th className="px-3 py-2 font-medium text-right">Left on table</th>
                </tr>
              </thead>
              <tbody>
                {skuRollups.map(r => {
                  const isOpen = expandedSku.has(r.sku);
                  const lines = filteredRows.filter(fr => fr.productName === r.sku);
                  return (
                    <Fragment key={r.sku}>
                      <tr
                        className="border-t border-border cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleSet(setExpandedSku, r.sku)}
                      >
                        <td className="w-8 px-2 py-2 text-muted-foreground">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2 text-foreground">{r.sku}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.vendor}</td>
                        <td className="px-3 py-2 text-right text-foreground">{r.lines}</td>
                        <td className="px-3 py-2 text-right text-foreground">{r.seats}</td>
                        <td className="px-3 py-2 text-right text-foreground">{fmtMoney(r.monthlyBill)}</td>
                        <td className="px-3 py-2 text-right text-foreground">{fmtMoney(r.monthlyCost)}</td>
                        <td className={`px-3 py-2 text-right ${r.monthlyMargin < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{fmtMoney(r.monthlyMargin)}</td>
                        <td className="px-3 py-2 text-right text-foreground">{r.belowCostLines}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${r.monthlyBleedIfBelowCost > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                          {r.monthlyBleedIfBelowCost > 0 ? fmtMoney(r.monthlyBleedIfBelowCost) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground">{r.belowMsrpLines}</td>
                        <td className={`px-3 py-2 text-right ${r.monthlyLeftOnTableIfBelowMsrp > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                          {r.monthlyLeftOnTableIfBelowMsrp > 0 ? fmtMoney(r.monthlyLeftOnTableIfBelowMsrp) : '—'}
                        </td>
                      </tr>
                      {isOpen && <LineDetailTable rows={lines} hideProduct colSpan={12} />}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'byClient' && (
        <div className="border border-border rounded-lg bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium text-right">Lines</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly bill</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly cost</th>
                  <th className="px-3 py-2 font-medium text-right">Margin</th>
                  <th className="px-3 py-2 font-medium text-right">Margin %</th>
                  <th className="px-3 py-2 font-medium text-right">Below cost</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly bleed</th>
                  <th className="px-3 py-2 font-medium text-right">Yearly bleed</th>
                  <th className="px-3 py-2 font-medium text-right">Below MSRP</th>
                  <th className="px-3 py-2 font-medium text-right">Left on table</th>
                </tr>
              </thead>
              <tbody>
                {clientRollups.map(c => {
                  const isOpen = expandedClient.has(c.companyId);
                  const lines = filteredRows.filter(fr => fr.companyId === c.companyId);
                  return (
                    <Fragment key={c.companyId}>
                      <tr
                        className="border-t border-border cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleSet(setExpandedClient, c.companyId)}
                      >
                        <td className="w-8 px-2 py-2 text-muted-foreground">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2 text-foreground">{c.companyName}</td>
                        <td className="px-3 py-2 text-right text-foreground">{c.lines}</td>
                        <td className="px-3 py-2 text-right text-foreground">{fmtMoney(c.monthlyBill)}</td>
                        <td className="px-3 py-2 text-right text-foreground">{fmtMoney(c.monthlyCost)}</td>
                        <td className={`px-3 py-2 text-right ${c.monthlyMargin < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{fmtMoney(c.monthlyMargin)}</td>
                        <td className={`px-3 py-2 text-right ${c.marginPct < 5 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{fmtPct(c.marginPct)}</td>
                        <td className="px-3 py-2 text-right text-foreground">{c.belowCostLines}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${c.monthlyBleedIfBelowCost > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                          {c.monthlyBleedIfBelowCost > 0 ? fmtMoney(c.monthlyBleedIfBelowCost) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right ${c.monthlyBleedIfBelowCost > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                          {c.monthlyBleedIfBelowCost > 0 ? fmtMoney(c.monthlyBleedIfBelowCost * 12) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground">{c.belowMsrpLines}</td>
                        <td className={`px-3 py-2 text-right ${c.monthlyLeftOnTableIfBelowMsrp > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                          {c.monthlyLeftOnTableIfBelowMsrp > 0 ? fmtMoney(c.monthlyLeftOnTableIfBelowMsrp) : '—'}
                        </td>
                      </tr>
                      {isOpen && <LineDetailTable rows={lines} hideClient colSpan={12} />}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'byRenewal' && (
        <div className="border border-border rounded-lg bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="px-3 py-2 font-medium">Renewal window</th>
                  <th className="px-3 py-2 font-medium text-right">Lines</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly bleed (below cost)</th>
                  <th className="px-3 py-2 font-medium text-right">Monthly left on table (below MSRP)</th>
                </tr>
              </thead>
              <tbody>
                {renewalBuckets.map(b => {
                  const isOpen = expandedRenewal.has(b.monthKey);
                  // Only lines that contributed to this bucket's totals (matches rollupByRenewal filter).
                  const lines = filteredRows.filter(fr => {
                    if (!fr.belowCost && fr.msrpFlag !== 'BELOW_MSRP') return false;
                    const key = fr.renewalDate ? fr.renewalDate.slice(0, 7) : 'no-commit';
                    return key === b.monthKey;
                  });
                  return (
                    <Fragment key={b.monthKey}>
                      <tr
                        className="border-t border-border cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleSet(setExpandedRenewal, b.monthKey)}
                      >
                        <td className="w-8 px-2 py-2 text-muted-foreground">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2 text-foreground">{b.monthLabel}</td>
                        <td className="px-3 py-2 text-right text-foreground">{b.lines}</td>
                        <td className={`px-3 py-2 text-right ${b.monthlyBleed > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                          {b.monthlyBleed > 0 ? fmtMoney(b.monthlyBleed) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right ${b.monthlyLeftOnTable > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                          {b.monthlyLeftOnTable > 0 ? fmtMoney(b.monthlyLeftOnTable) : '—'}
                        </td>
                      </tr>
                      {isOpen && <LineDetailTable rows={lines} colSpan={5} />}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-3 text-xs text-muted-foreground border-t border-border">
            Locked commitments can't be repriced until their renewal month. Lines with no commitment can be adjusted at any time.
          </div>
        </div>
      )}

      {tab === 'lines' && (
        <div className="border border-border rounded-lg bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium text-right">Qty</th>
                  <th className="px-3 py-2 font-medium text-right">Sell</th>
                  <th className="px-3 py-2 font-medium text-right">Cost</th>
                  <th className="px-3 py-2 font-medium text-right">MSRP</th>
                  <th className="px-3 py-2 font-medium text-right">Margin/mo</th>
                  <th className="px-3 py-2 font-medium text-right">Margin %</th>
                  <th className="px-3 py-2 font-medium">MSRP</th>
                  <th className="px-3 py-2 font-medium">Commit</th>
                  <th className="px-3 py-2 font-medium">Renews</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 500).map(r => {
                  const badge = msrpBadge(r.msrpFlag);
                  return (
                    <tr key={r.subscriptionId} className={`border-t border-border ${r.belowCost ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                      <td className="px-3 py-2 text-foreground whitespace-nowrap">{r.companyName}</td>
                      <td className="px-3 py-2 text-foreground">{r.productName}</td>
                      <td className="px-3 py-2 text-right text-foreground">{r.quantity}</td>
                      <td className="px-3 py-2 text-right text-foreground">{fmtMoney(r.unitSellPrice, 4)}</td>
                      <td className="px-3 py-2 text-right text-foreground">{fmtMoney(r.unitCost, 4)}</td>
                      <td className="px-3 py-2 text-right text-foreground">{r.unitMsrp === null ? '—' : fmtMoney(r.unitMsrp, 4)}</td>
                      <td className={`px-3 py-2 text-right ${r.monthlyMargin < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-foreground'}`}>
                        {fmtMoney(r.monthlyMargin)}
                      </td>
                      <td className={`px-3 py-2 text-right ${r.marginPct < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                        {fmtPct(r.marginPct)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.commitmentTerm || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtMonth(r.renewalDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredRows.length > 500 && (
            <div className="p-3 text-xs text-muted-foreground border-t border-border">
              Showing 500 of {filteredRows.length.toLocaleString()} lines. Narrow the filter or export the CSV to see the rest.
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-muted-foreground border-t border-border pt-3">
        Below-MSRP total across the filtered set: <span className="text-foreground font-medium">{fmtMoney(totalLeftOnTable)}/mo</span>. Below-cost total: <span className="text-foreground font-medium">{fmtMoney(totalBleed)}/mo</span>.
      </div>
    </div>
  );
}
